// Google Drive API Service for media storage

class GoogleDriveService {
  constructor() {
    this.isInitialized = false;
    this.isSignedIn = false;
    this.accessToken = null;
  }

  // Initialize Google API client
  async initialize() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('client:auth2', async () => {
          try {
            await window.gapi.client.init({
              apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
              clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
              scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly'
            });

            this.isInitialized = true;
            this.isSignedIn = window.gapi.auth2.getAuthInstance().isSignedIn.get();
            
            // Listen for sign-in state changes
            window.gapi.auth2.getAuthInstance().isSignedIn.listen((signedIn) => {
              this.isSignedIn = signedIn;
              if (signedIn) {
                this.accessToken = window.gapi.auth2.getAuthInstance().currentUser.get().getAuthResponse().access_token;
              }
            });

            if (this.isSignedIn) {
              this.accessToken = window.gapi.auth2.getAuthInstance().currentUser.get().getAuthResponse().access_token;
            }

            resolve(true);
          } catch (error) {
            console.error('Error initializing Google API:', error);
            reject(error);
          }
        });
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  // Sign in to Google
  async signIn() {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await window.gapi?.auth2?.getAuthInstance()?.signIn();
      this.isSignedIn = true;
      this.accessToken = window.gapi?.auth2?.getAuthInstance()?.currentUser?.get()?.getAuthResponse()?.access_token;
      return true;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  // Sign out from Google
  async signOut() {
    if (!this.isInitialized) return;

    try {
      await window.gapi?.auth2?.getAuthInstance()?.signOut();
      this.isSignedIn = false;
      this.accessToken = null;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Check if user is signed in
  async checkSignInStatus() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.isSignedIn;
  }

  // List folders in Google Drive
  async listFolders(parentId = 'root') {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      const response = await window.gapi?.client?.drive?.files?.list({
        q: `'${parentId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
        fields: 'files(id, name, thumbnailLink, modifiedTime)',
        orderBy: 'name'
      });

      return response?.result?.files || [];
    } catch (error) {
      console.error('Error listing folders:', error);
      throw error;
    }
  }

  // Get folder details with file count
  async getFolderDetails(folderId) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      // Get folder info
      const folderResponse = await window.gapi?.client?.drive?.files?.get({
        fileId: folderId,
        fields: 'id, name, thumbnailLink, modifiedTime'
      });

      // Count files in folder
      const filesResponse = await window.gapi?.client?.drive?.files?.list({
        q: `'${folderId}' in parents and trashed=false`,
        fields: 'files(id)',
        pageSize: 1000
      });

      return {
        ...folderResponse?.result,
        fileCount: filesResponse?.result?.files?.length || 0
      };
    } catch (error) {
      console.error('Error getting folder details:', error);
      throw error;
    }
  }

  // List media files in a folder
  async listMediaInFolder(folderId) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      const response = await window.gapi?.client?.drive?.files?.list({
        q: `'${folderId}' in parents and (mimeType contains 'image/' or mimeType contains 'video/') and trashed=false`,
        fields: 'files(id, name, mimeType, size, thumbnailLink, webContentLink, webViewLink, modifiedTime, imageMediaMetadata, videoMediaMetadata)',
        orderBy: 'modifiedTime desc',
        pageSize: 1000
      });

      return response?.result?.files || [];
    } catch (error) {
      console.error('Error listing media files:', error);
      throw error;
    }
  }

  // Upload file to Google Drive
  async uploadFile(file, folderId, metadata = {}) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      const fileMetadata = {
        name: file?.name,
        parents: [folderId],
        ...metadata
      };

      const form = new FormData();
      form?.append('metadata', new Blob([JSON.stringify(fileMetadata)], { type: 'application/json' }));
      form?.append('file', file);

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,size,thumbnailLink,webContentLink,webViewLink', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: form
      });

      if (!response?.ok) {
        throw new Error('Upload failed');
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Make file publicly accessible and get shareable URL
  async makeFilePublic(fileId) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      // Create public permission
      await window.gapi?.client?.drive?.permissions?.create({
        fileId: fileId,
        resource: {
          role: 'reader',
          type: 'anyone'
        }
      });

      // Get file details with webContentLink
      const response = await window.gapi?.client?.drive?.files?.get({
        fileId: fileId,
        fields: 'id, webContentLink, webViewLink, thumbnailLink'
      });

      return response?.result;
    } catch (error) {
      console.error('Error making file public:', error);
      throw error;
    }
  }

  // Get direct download URL for a file
  getDirectDownloadUrl(fileId) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }

  // Get thumbnail URL for a file
  getThumbnailUrl(fileId, size = 400) {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
  }

  // Get embeddable image URL
  getEmbedUrl(fileId) {
    return `https://drive.google.com/uc?export=view&id=${fileId}`;
  }

  // Delete file from Google Drive
  async deleteFile(fileId) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      await window.gapi?.client?.drive?.files?.delete({
        fileId: fileId
      });
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Create a new folder
  async createFolder(folderName, parentId = 'root') {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      const response = await window.gapi?.client?.drive?.files?.create({
        resource: {
          name: folderName,
          mimeType: 'application/vnd.google-apps.folder',
          parents: [parentId]
        },
        fields: 'id, name'
      });

      return response?.result;
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  // Sync folder contents and return media metadata
  async syncFolder(folderId) {
    if (!this.isSignedIn) {
      throw new Error('Not signed in to Google Drive');
    }

    try {
      const files = await this.listMediaInFolder(folderId);
      
      // Transform to application format
      return files?.map(file => ({
        driveFileId: file?.id,
        fileName: file?.name,
        fileSize: parseInt(file?.size) || 0,
        mimeType: file?.mimeType,
        mediaType: file?.mimeType?.startsWith('image/') ? 'photo' : 'video',
        fileUrl: this.getEmbedUrl(file?.id),
        thumbnailUrl: file?.thumbnailLink || this.getThumbnailUrl(file?.id),
        downloadUrl: this.getDirectDownloadUrl(file?.id),
        webViewLink: file?.webViewLink,
        modifiedTime: file?.modifiedTime,
        metadata: {
          image: file?.imageMediaMetadata,
          video: file?.videoMediaMetadata
        }
      }));
    } catch (error) {
      console.error('Error syncing folder:', error);
      throw error;
    }
  }
}

export const googleDriveService = new GoogleDriveService();